import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2, CheckCircle2 } from "lucide-react";
import { CONTACT } from "@/lib/data";
import { useI18n } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  { value: "Business Waste Service", key: "lead.svc.business" },
  { value: "Roll-Off Dumpster Service", key: "lead.svc.rolloff" },
  { value: "Recycling", key: "lead.svc.recycling" },
  { value: "Other / Not Sure", key: "lead.svc.other" },
];

// Container sizes + frequencies depend on the chosen service
const SERVICE_CONFIG = {
  "Business Waste Service": {
    sizes: ["1 Yard", "2 Yard", "3 Yard", "4 Yard", "6 Yard", "8 Yard"],
    frequencies: [
      "1x per week", "2x per week", "3x per week", "4x per week",
      "5x per week", "6x per week", "7x per week",
    ],
    sizeLabelKey: "lead.sizeLabel.business",
  },
  "Roll-Off Dumpster Service": {
    sizes: ["10 Yard", "20 Yard", "30 Yard", "40 Yard"],
    frequencies: ["On-Call", "1x per week", "2x per week", "Bi-Weekly", "1x per month"],
    sizeLabelKey: "lead.sizeLabel.rolloff",
  },
};

const initial = { name: "", business: "", email: "", phone: "", service: "", container_size: "", frequency: "", quantity: "", message: "" };

export default function LeadForm({ heading, compact = false }) {
  const { t } = useI18n();
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const resolvedHeading = heading || t("lead.defaultHeading");

  // When service changes, reset the dependent fields so stale options aren't submitted
  const onServiceChange = (e) => {
    setForm({ ...form, service: e.target.value, container_size: "", frequency: "", quantity: "" });
  };

  const cfg = SERVICE_CONFIG[form.service];

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.service) {
      toast.error(t("lead.validation"));
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form, quantity: form.quantity ? Number(form.quantity) : null };
      await axios.post(`${API}/quote`, payload);
      toast.success(t("lead.successToast"));
      setDone(true);
      setForm(initial);
    } catch (err) {
      toast.error(t("lead.errorToast") + CONTACT.phone);
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full px-4 py-3.5 rounded-xl bg-[#FBFAF8] border border-[#E7E5DF] text-[#0B0F14] placeholder:text-slate-400 focus:outline-none focus:border-emerald-600/60 transition-colors text-sm";

  if (done) {
    return (
      <div className="py-14 text-center bg-white border border-[#E7E5DF] rounded-3xl shadow-soft px-6" data-testid="lead-success">
        <CheckCircle2 size={54} className="text-emerald-600 mx-auto mb-5" />
        <h3 className="font-display font-bold text-2xl text-[#0B0F14] mb-2">{t("lead.doneTitle")}</h3>
        <p className="text-slate-600 text-sm max-w-sm mx-auto">
          {t("lead.doneBody")}
          <a href={CONTACT.phoneHref} className="text-emerald-700 font-semibold">{CONTACT.phone}</a>.
        </p>
        <button onClick={() => setDone(false)} className="mt-7 px-6 py-3 rounded-full border border-[#E7E5DF] text-[#0B0F14] text-sm font-semibold hover:border-emerald-600 transition-colors" data-testid="lead-reset-btn">
          {t("lead.another")}
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="bg-white border border-[#E7E5DF] rounded-3xl shadow-soft p-6 sm:p-8 space-y-4" data-testid="lead-form">
      {resolvedHeading && <h3 className="font-display font-bold text-xl text-[#0B0F14] mb-1">{resolvedHeading}</h3>}
      <div className="grid sm:grid-cols-2 gap-4">
        <input data-testid="lead-name" required autoComplete="name" aria-label={t("lead.name")} className={inputCls} placeholder={t("lead.name")} value={form.name} onChange={update("name")} />
        <input data-testid="lead-business" autoComplete="organization" aria-label={t("lead.business")} className={inputCls} placeholder={t("lead.business")} value={form.business} onChange={update("business")} />
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <input data-testid="lead-email" required type="email" autoComplete="email" aria-label={t("lead.email")} className={inputCls} placeholder={t("lead.email")} value={form.email} onChange={update("email")} />
        <input data-testid="lead-phone" required type="tel" autoComplete="tel" aria-label={t("lead.phone")} className={inputCls} placeholder={t("lead.phone")} value={form.phone} onChange={update("phone")} />
      </div>
      <select data-testid="lead-service" required aria-label={t("lead.serviceNeeded")} className={`${inputCls} ${form.service ? "text-[#0B0F14]" : "text-slate-400"}`} value={form.service} onChange={onServiceChange}>
        <option value="">{t("lead.serviceNeeded")}</option>
        {SERVICE_OPTIONS.map((o) => <option key={o.value} value={o.value} className="text-[#0B0F14]">{t(o.key)}</option>)}
      </select>

      {cfg && (
        <div className="grid sm:grid-cols-3 gap-4">
          <select data-testid="lead-container-size" className={`${inputCls} ${form.container_size ? "text-[#0B0F14]" : "text-slate-400"}`} value={form.container_size} onChange={update("container_size")}>
            <option value="">{t(cfg.sizeLabelKey)}</option>
            {cfg.sizes.map((s) => <option key={s} value={s} className="text-[#0B0F14]">{s}</option>)}
          </select>
          <select data-testid="lead-frequency" className={`${inputCls} ${form.frequency ? "text-[#0B0F14]" : "text-slate-400"}`} value={form.frequency} onChange={update("frequency")}>
            <option value="">{t("lead.frequency")}</option>
            {cfg.frequencies.map((f) => <option key={f} value={f} className="text-[#0B0F14]">{f}</option>)}
          </select>
          <select data-testid="lead-quantity" className={`${inputCls} ${form.quantity ? "text-[#0B0F14]" : "text-slate-400"}`} value={form.quantity} onChange={update("quantity")}>
            <option value="">{t("lead.quantity")}</option>
            {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => <option key={n} value={n} className="text-[#0B0F14]">{n} {n > 1 ? t("lead.containersWord") : t("lead.containerWord")}</option>)}
          </select>
        </div>
      )}

      <textarea data-testid="lead-message" aria-label={t("lead.message")} rows={compact ? 3 : 4} className={`${inputCls} resize-none`} placeholder={t("lead.message")} value={form.message} onChange={update("message")} />
      <button type="submit" disabled={loading} data-testid="lead-submit"
        className="w-full flex items-center justify-center gap-2.5 px-6 py-4 rounded-xl bg-[#0E3B3B] text-white font-bold hover:bg-[#0A2C2C] active:scale-[0.99] transition-all disabled:opacity-60">
        {loading ? <><Loader2 size={18} className="animate-spin" /> {t("lead.submitting")}</> : <><Send size={18} /> {t("lead.submit")}</>}
      </button>
      <p className="text-center font-mono-tech text-[11px] text-slate-500 uppercase tracking-widest">{t("lead.priceNote")}</p>
    </form>
  );
}
